import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  RefreshCw,
  Search,
  Briefcase,
  Plus,
  Eye,
  Trash2,
  Edit3,
  ShieldCheck,
  X,
} from "lucide-react";

import { PERMS } from "../../../utils/perms";
import { useAccess } from "../../../utils/useAccess";
import PageGate from "../../../components/PageGate";
import AccessGate from "../../../components/AccessGate";

import {
  Designation,
  useCreateDesignationMutation,
  useDeleteDesignationMutation,
  useGetDesignationsQuery,
  useUpdateDesignationMutation,
} from "../api/designationsApi";

// --- Sub-components ---
function StatCard({
  title,
  value,
  hint,
}: {
  title: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="bg-white border rounded-xl p-5 shadow-sm">
      <div className="text-sm text-gray-600">{title}</div>
      <div className="text-2xl font-semibold text-gray-900 mt-1">{value}</div>
      {hint ? <div className="text-xs text-gray-500 mt-2">{hint}</div> : null}
    </div>
  );
}

function errMsg(e: any) {
  return e?.data?.message || e?.data?.error || e?.error || "Request failed";
}

export default function DesignationsPage() {
  const navigate = useNavigate();
  const { user, can } = useAccess();

  // API Hooks
  const { data = [], isLoading, error, refetch } = useGetDesignationsQuery();
  const [createDesignation, { isLoading: creating }] =
    useCreateDesignationMutation();
  const [updateDesignation, { isLoading: updating }] =
    useUpdateDesignationMutation();
  const [deleteDesignation, { isLoading: deleting }] =
    useDeleteDesignationMutation();

  // Local UI State
  const [q, setQ] = useState("");
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState<Designation | null>(null);

  const [name, setName] = useState("");
  const [editName, setEditName] = useState("");

  const [localErr, setLocalErr] = useState<string | null>(null);
  const [localOk, setLocalOk] = useState<string | null>(null);

  // Filters & Sorting
  const filteredRows = useMemo(() => {
    const nq = q.trim().toLowerCase();
    return data
      .filter(
        (d) =>
          d.name.toLowerCase().includes(nq) ||
          (d.role?.name && d.role.name.toLowerCase().includes(nq))
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [data, q]);

  const stats = useMemo(
    () => ({
      total: data.length,
      withRoles: data.filter((d) => d.role).length,
    }),
    [data]
  );

  // --- Handlers ---
  const onAdd = async () => {
    setLocalErr(null);
    setLocalOk(null);
    if (!name.trim()) return;
    try {
      await createDesignation({ name: name.trim() }).unwrap();
      setName("");
      setOpenAdd(false);
      setLocalOk("Designation created successfully.");
    } catch (e) {
      setLocalErr(errMsg(e));
    }
  };

  const onEdit = async () => {
    if (!openEdit || !editName.trim()) return;
    setLocalErr(null);
    setLocalOk(null);
    try {
      await updateDesignation({
        id: openEdit.id,
        name: editName.trim(),
      }).unwrap();
      setOpenEdit(null);
      setLocalOk("Designation updated.");
    } catch (e) {
      setLocalErr(errMsg(e));
    }
  };

  const onDelete = async (d: Designation) => {
    if (
      !window.confirm(
        `Delete designation "${d.name}"?\nThis might affect mapped roles and user assignments.`
      )
    )
      return;
    setLocalErr(null);
    setLocalOk(null);
    try {
      await deleteDesignation(d.id).unwrap();
      setLocalOk("Designation removed.");
    } catch (e) {
      setLocalErr(errMsg(e));
    }
  };

  return (
    <PageGate perm={PERMS.designation.access}>
      <div className="space-y-6">
        {/* Header Card */}
        <div className="bg-white border rounded-xl p-5 shadow-sm">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-gray-50 border">
                <Briefcase className="w-5 h-5 text-gray-700" />
              </div>
              <div>
                <div className="font-semibold text-gray-900">Designations</div>
                <div className="text-sm text-gray-600">
                  Signed in as{" "}
                  <span className="font-medium text-gray-900">
                    {user?.email}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => refetch()}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border bg-white hover:bg-gray-50 text-sm transition-colors"
                disabled={isLoading}
              >
                <RefreshCw
                  className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`}
                />
                Refresh
              </button>

              <button
                onClick={() => {
                  setLocalErr(null);
                  setOpenAdd(true);
                }}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-900 text-white hover:bg-gray-800 text-sm transition-transform active:scale-95"
              >
                <Plus className="w-4 h-4" />
                Add Designation
              </button>
            </div>
          </div>

          {/* Search Bar */}
          <div className="mt-5 relative max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search designations or roles..."
              className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-gray-200 transition-all"
            />
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard title="Total Designations" value={String(stats.total)} />
          <StatCard title="Mapped to Roles" value={String(stats.withRoles)} />
          <StatCard
            title="Filter Results"
            value={String(filteredRows.length)}
          />
        </div>

        {/* Alerts */}
        {localErr && (
          <div className="p-3 rounded-lg border border-red-200 bg-red-50 text-red-700 text-sm flex justify-between items-center">
            <span>{localErr}</span>
            <button onClick={() => setLocalErr(null)}>
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        {localOk && (
          <div className="p-3 rounded-lg border border-green-200 bg-green-50 text-green-700 text-sm flex justify-between items-center">
            <span>{localOk}</span>
            <button onClick={() => setLocalOk(null)}>
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Table Card */}
        <div className="bg-white border rounded-xl p-6 shadow-sm">
          {isLoading ? (
            <div className="py-12 text-center text-gray-500 flex flex-col items-center gap-2">
              <RefreshCw className="w-6 h-6 animate-spin text-gray-300" />
              <span>Loading designations...</span>
            </div>
          ) : error ? (
            <div className="py-12 text-center text-red-600 font-medium">
              {errMsg(error)}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead>
                  <tr className="border-b text-gray-400 font-medium uppercase text-[11px] tracking-wider">
                    <th className="py-3 px-2 w-10">#</th>
                    <th className="py-3 px-4">Designation Name</th>
                    <th className="py-3 px-4">Mapped Spatie Role</th>
                    <th className="py-3 px-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filteredRows.map((d, idx) => (
                    <tr
                      key={d.id}
                      className="hover:bg-gray-50 transition-colors group"
                    >
                      <td className="py-4 px-2 text-gray-400 font-mono text-xs">
                        {(idx + 1).toString().padStart(2, "0")}
                      </td>
                      <td className="py-4 px-4">
                        <div className="font-semibold text-gray-800">
                          {d.name}
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        {d.role ? (
                          <div className="flex items-center gap-1.5 text-blue-600 bg-blue-50 px-2.5 py-1 rounded-full w-fit text-[11px] font-bold border border-blue-100 uppercase">
                            <ShieldCheck className="w-3 h-3" />
                            {d.role.name}
                          </div>
                        ) : (
                          <span className="text-gray-300 italic text-xs">
                            Unmapped
                          </span>
                        )}
                      </td>
                      <td className="py-4 px-4 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => navigate(`/designations/${d.id}`)}
                            className="p-1.5 border rounded-lg bg-white hover:bg-gray-50 text-gray-600 transition-colors shadow-sm"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>

                          <button
                            onClick={() => {
                              setOpenEdit(d);
                              setEditName(d.name);
                              setLocalErr(null);
                            }}
                            className="p-1.5 border rounded-lg bg-white hover:bg-blue-50 text-blue-600 transition-colors shadow-sm"
                            title="Edit Designation"
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>

                          <button
                            onClick={() => onDelete(d)}
                            disabled={deleting}
                            className="p-1.5 border rounded-lg bg-white hover:bg-red-50 text-red-600 transition-colors shadow-sm disabled:opacity-50"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredRows.length === 0 && (
                <div className="py-16 text-center text-gray-400 text-sm italic">
                  No designations match your search.
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* --- ADD MODAL --- */}
      {openAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 backdrop-blur-sm p-4">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl border p-6 animate-in fade-in zoom-in duration-200">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">
                New Designation
              </h2>
              <button
                onClick={() => setOpenAdd(false)}
                className="p-1 hover:bg-gray-100 rounded-full"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <p className="text-sm text-gray-500 mt-1">
              Add a new job title to your organization.
            </p>

            <div className="mt-6">
              <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">
                Name
              </label>
              <input
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && onAdd()}
                className="mt-1 w-full border rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-gray-900/5 focus:border-gray-900 transition-all"
                placeholder="e.g. Senior Technical Lead"
              />
            </div>

            <div className="mt-8 flex gap-3">
              <button
                onClick={() => setOpenAdd(false)}
                className="flex-1 px-4 py-3 rounded-xl border font-medium hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={onAdd}
                disabled={creating || !name.trim()}
                className="flex-1 px-4 py-3 rounded-xl bg-gray-900 text-white font-medium hover:bg-gray-800 disabled:opacity-50 transition-all shadow-lg shadow-gray-900/20"
              >
                {creating ? "Creating..." : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- EDIT MODAL --- */}
      {openEdit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 backdrop-blur-sm p-4">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl border p-6 animate-in fade-in zoom-in duration-200">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">
                Edit Designation
              </h2>
              <button
                onClick={() => setOpenEdit(null)}
                className="p-1 hover:bg-gray-100 rounded-full"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <p className="text-sm text-gray-500 mt-1">
              Update the name for this designation.
            </p>

            <div className="mt-6">
              <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">
                Name
              </label>
              <input
                autoFocus
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && onEdit()}
                className="mt-1 w-full border rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-gray-900/5 focus:border-gray-900 transition-all"
              />
            </div>

            <div className="mt-8 flex gap-3">
              <button
                onClick={() => setOpenEdit(null)}
                className="flex-1 px-4 py-3 rounded-xl border font-medium hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={onEdit}
                disabled={updating || !editName.trim()}
                className="flex-1 px-4 py-3 rounded-xl bg-gray-900 text-white font-medium hover:bg-gray-800 disabled:opacity-50 transition-all shadow-lg shadow-gray-900/20"
              >
                {updating ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </PageGate>
  );
}
